import { type MouseEvent, useCallback } from "react";
import { useUnitMode } from "../../useUnitMode";
import { useDispatch } from "react-redux";
import { changeUnitModeAction } from "../../redux/ImperialMetricDataReducer";
import { setUnitCookie } from "../../unitCookie";
import { UnitMode } from "../../redux/UnitMode";
import { trackUnitsInteraction } from "../../../feature/Analytics/ga/ga";
import { useTranslation } from "react-i18next";
import { ToggleButton, ToggleButtonGroup } from "~/common/m3/Button/ToggleButton";
import { InputLabel, type ToggleButtonGroupProps } from "@mui/material";
import styled from "@emotion/styled";

type GAPageType = "Navigation" | "Search" | "PDP" | "PLP" | "Comparison List" | "SA";

interface Props extends Omit<JSX.IntrinsicElements["div"], "children" | "onChange"> {
  value: UnitMode;
  onChange: (unit: UnitMode) => void;
}

export const ToggleContainer = styled("div")({
  maxWidth: "354px", //Max width added on request from SCHAEFF-34146
});

export const ImperialToggle = ({ onChange, value, ...props }: Props) => {
  const { t } = useTranslation();

  // @ts-ignore
  const handleOnclick: ToggleButtonGroupProps["onClick"] = (e: MouseEvent) => {
    const value = (e.target as HTMLElement).getAttribute("value") as UnitMode;
    onChange(value);
  };

  return (
    <ToggleContainer {...props}>
      <InputLabel shrink>{t("feature.toggle.unitSystem")}</InputLabel>
      <ToggleButtonGroup fullWidth value={value} showCheckmarks={true} onClick={handleOnclick}>
        <ToggleButton value={UnitMode.Metric}>{t("common.measureSystem.metric")}</ToggleButton>
        <ToggleButton value={UnitMode.Imperial}>{t("common.measureSystem.imperial")}</ToggleButton>
      </ToggleButtonGroup>
    </ToggleContainer>
  );
};

interface ReduxImperialToggleProps extends Omit<Props, "onChange" | "value"> {
  page: GAPageType;
  onChange?: (unitMode: UnitMode) => void;
}

export const ReduxImperialToggle = ({ page, onChange, ...props }: ReduxImperialToggleProps) => {
  const { value, onChange: handleOnChange } = useReduxUnitMode(page, onChange);
  return <ImperialToggle value={value} onChange={handleOnChange} {...props} />;
};

export const useReduxUnitMode = (page: GAPageType, onChange?: (unitMode: UnitMode) => void) => {
  const value = useUnitMode();
  const dispatch = useDispatch();
  const handleOnChange = useCallback(
    (unitMode: UnitMode) => {
      dispatch(changeUnitModeAction(unitMode));
      setUnitCookie(unitMode);
      trackUnitsInteraction(page, unitMode);
      onChange?.(unitMode);
    },
    [dispatch, page, onChange],
  );
  const onClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      const unitMode = (e.target as HTMLElement).getAttribute("value")! as UnitMode;
      handleOnChange(unitMode);
    },
    [handleOnChange],
  );
  return {
    value,
    onClick,
    onChange: handleOnChange,
  };
};
